// Work kit — navigator ENGINE controller (wave 11). Mounted FIRST on the
// navigator root ("ui--work-navigator", url-state second in the fixed
// stack). It owns every interaction the composed chrome exposes and speaks
// EXACTLY the spec §3.2 event grammar — bubbling ui--work:<verb>
// CustomEvents dispatched on the root, zero HTTP, zero localStorage, zero
// DOM reordering of rows (N-15) or columns (server-truth per render):
//
//   rail        — quick pills (aria-pressed flip + filter-toggle), per-value
//                 remove pills (filter-change with the remaining values),
//                 clear-all (filters-clear, EMPTY payload), the in-page
//                 filter box (a HOST seam: `/` focuses it, the box never
//                 emits — URL filter values are option identifiers, pinned
//                 N-17; a free-text commit is host glue, never kit HTTP),
//                 and the draft+Apply drawer (N-42:
//                 checkbox edits STAGE locally; Apply diffs the draft
//                 against data-filter-committed and commits one filter-change
//                 per changed key; Cancel discards and resets).
//   toolbar     — sort headers (click cycles null → asc → desc → null →
//                 sort-change; aria-sort mirrors the cycle), the columns
//                 picker (toggleColumn / moveColumn via data-action; the
//                 engine guards against hiding the last visible column).
//   rows        — click on a row (outside interactive descendants — the
//                 N-37 exclusion) and j/k/Enter/o/Space/x keyboard (§3.5):
//                 issue-open {issue_id}, selection-request {issue_id, mode}
//                 through the W3 seam (W11-D6 stub-and-swap), `/` focuses
//                 the filter box, Escape closes ONE open surface per press
//                 (nearest-first: the picker before the drawer).
//
// State source (N-09): visible_keys/column state hydrate from the root's
// data-ui--work-navigator-*-value attributes and live ONLY in memory —
// nothing is cached across renders, localStorage is a failure.

import { Controller } from "@hotwired/stimulus"

const INTERACTIVE_EXCLUSION = [
  "button", "a", "input", "select", "textarea", "label", "summary",
  "[data-dropdown-item]", "[role='menuitem']", "[role='menuitemcheckbox']",
  "[data-work-interactive]",
].join(", ")

const INPUT_EXCLUSION = "input, textarea, select, [contenteditable='true'], [contenteditable='']"

// Open-surface probes (§3.5). The columns picker is open when its menu
// content carries data-state="open"; the filter drawer is open when its
// ui--drawer root is VISIBLE — DrawerComponent keeps `hidden` on the root
// while closed (show() removes it, close() re-adds it in its 500ms timer).
const PICKER_OPEN_SELECTOR =
  "[data-work-columns-picker] [data-ui--dropdown-menu-target='content'][data-state='open']"
const DRAWER_OPEN_SELECTOR =
  "[data-work-filter-drawer] [data-controller~='ui--drawer']:not(.hidden)"

export default class extends Controller {
  static targets = ["announcer"]

  static values = {
    allKeys: {type: Array, default: []},
    visibleKeys: {type: Array, default: []},
    sort: {type: String, default: ""},
    quickFilters: {type: Array, default: []},
    filterValues: {type: Object, default: {}},
    // Last-column-guard announce copy, hydrated from
    // work.navigator.columns.last_visible (comments_controller
    // savedMessage precedent — English fallback for standalone mounts).
    lastColumnMessage: {type: String, default: ""},
  }

  connect() {
    this._visibleKeys = [...this.visibleKeysValue]
    this._sort = this._parseSort(this.sortValue)
    this._draft = this._committedDraft()
    this._rowOrdinal = -1
    // F-4 closing-window guard: drawer roots this engine has asked to close,
    // still inside the drawer's 500ms slide-out window. Cleared per drawer
    // on its drawer:close event (see _onDrawerClosed).
    this._closingDrawers = new WeakSet()

    this._bind()
    this._initRovingTabindex()
  }

  disconnect() {
    this._unbind()
    this._handlers = null
  }

  // --- lifecycle wiring (re-bound on every connect — Turbo safe, N-22) ---

  _bind() {
    this._handlers = [
      {type: "click", handler: (event) => this._onClick(event)},
      {type: "change", handler: (event) => this._onDraftChange(event)},
      {type: "keydown", handler: (event) => this._onKeydown(event)},
      // drawer:close bubbles from a drawer root once its slide-out timer has
      // hidden it — the engine's signal that a closing cycle is COMPLETE.
      {type: "drawer:close", handler: (event) => this._onDrawerClosed(event)},
    ]
    this._handlers.forEach(({type, handler}) => this.element.addEventListener(type, handler))
  }

  _unbind() {
    this._handlers?.forEach(({type, handler}) => this.element.removeEventListener(type, handler))
  }

  // --- public actions (picker data-actions, N-08) ---

  // A picker checkbox toggled. ORDERING CONTRACT: this per-item action fires
  // BEFORE the dropdown controller's delegated flip (target listeners run
  // before its content-level listener), so aria-checked still reads the
  // PRE-click state and the intent is its inverse. The dropdown owns the
  // aria/indicator flip; the engine owns the visible set, the last-column
  // guard, and the columns-visibility emission.
  toggleColumn(event) {
    const item = event.currentTarget
    const key = item?.dataset?.workColumnKey
    if (!key) return
    const nowChecked = item.getAttribute("aria-checked") !== "true"

    if (!nowChecked) {
      // The last visible column cannot be unchecked — refuse the hide and
      // announce. The dropdown still flips the item on this click, so the
      // aria + indicator are reverted once that flip has happened.
      if (this._visibleKeys.length <= 1) {
        this._announce(this.lastColumnMessageValue || "At least one column stays visible")
        queueMicrotask(() => this._revertCheckbox(item))
        return
      }
      this._visibleKeys = this._visibleKeys.filter((visible) => visible !== key)
    } else if (!this._visibleKeys.includes(key)) {
      // Re-checking inserts the key at its CANONICAL position (the next
      // visible key that follows it in allKeys), never just appended.
      const canonical = this.allKeysValue
      let insertAt = this._visibleKeys.length
      for (let index = canonical.indexOf(key) + 1; index < canonical.length; index += 1) {
        const position = this._visibleKeys.indexOf(canonical[index])
        if (position !== -1) {
          insertAt = position
          break
        }
      }
      this._visibleKeys.splice(insertAt, 0, key)
    }
    this._emit("columns-visibility", {visible_keys: [...this._visibleKeys]})
  }

  // Undo the dropdown controller's flip for a refused hide (mirror of its
  // toggleCheckbox: aria-checked back to checked, checkmark indicator shown).
  _revertCheckbox(item) {
    item.setAttribute("aria-checked", "true")
    const indicator = item.querySelector("[data-dropdown-indicator]")?.firstElementChild
    indicator?.classList.remove("hidden")
  }

  // A picker reorder pair: permute the visible prefix, emit the wave-6
  // column-reorder shape {moved_key, before_key|null, after_key|null} plus
  // the derived columns-visibility (the URL writer persists order through
  // the columns param).
  moveColumn(event) {
    const button = event.currentTarget
    const key = button?.dataset?.workColumnKey
    const direction = button?.dataset?.workColumnMove
    if (!key || !direction) return

    const order = [...this._visibleKeys]
    const from = order.indexOf(key)
    if (from === -1) return
    const to = direction === "up" ? from - 1 : from + 1
    if (to < 0 || to >= order.length) return

    order.splice(to, 0, order.splice(from, 1)[0])
    this._visibleKeys = order
    this._emit("column-reorder", {
      moved_key: key,
      // after_key = the item the moved key now FOLLOWS (null at the top
      // edge); before_key = the item it now precedes (null at the bottom
      // edge) — the wave-6 payload contract, verbatim.
      after_key: to - 1 >= 0 ? order[to - 1] : null,
      before_key: to + 1 < order.length ? order[to + 1] : null,
    })
    this._emit("columns-visibility", {visible_keys: [...order]})
  }

  // --- delegated click routing ---

  _onClick(event) {
    const target = event.target

    // Rail: quick pills. The engine does NOT optimistically flip the pill's
    // aria-pressed (an earlier build did): the pill's pressed state and its
    // visual live together in the host's render (the visual rides the
    // wrapped Ui::PillComponent's color), and an aria flip without the
    // matching visual left pressed-aria/neutral-visual until the re-render.
    // The host re-render owns BOTH; the engine reads the pre-click state
    // for the intent and mirrors the optimistic state on its own hydration
    // value (N-16) without touching the DOM.
    const quickPill = target.closest("[data-work-filter-key]")
    if (quickPill) {
      const key = quickPill.dataset.workFilterKey
      const nowActive = quickPill.getAttribute("aria-pressed") !== "true"
      // Optimistic flip is VIEW state on the Stimulus value (never a private
      // field — N-16: the hydration value is not a cache, but a stale mirror
      // of it is worse than none).
      this.quickFiltersValue = this.quickFiltersValue
        .map((def) => (def.key === key ? {...def, active: nowActive} : def))
      this._emit("filter-toggle", {filter_key: key, active: nowActive})
      return
    }

    // Rail: per-value remove pills → filter-change with the REMAINING
    // committed values (the engine diffs; the pill itself carries nothing
    // but key+value).
    const removePill = target.closest("[data-work-filter-remove]")
    if (removePill) {
      const key = removePill.dataset.filterKey
      const value = removePill.dataset.filterValue
      if (key && value) {
        const committed = this.filterValuesValue[key] || []
        this._emit("filter-change", {
          filter_key: key,
          values: committed.filter((entry) => entry !== value),
        })
      }
      return
    }

    // Rail: clear-all → the EMPTY filters-clear event (never param
    // mutation — the URL writer owns params).
    if (target.closest("[data-work-filters-clear]")) {
      this._emit("filters-clear", {})
      return
    }

    // P-state: the error retry → the EMPTY navigator-retry intent; the
    // HOST refetches (zero HTTP in the kit, §3.4/N-26).
    if (target.closest("[data-work-navigator-retry]")) {
      this._emit("navigator-retry", {})
      return
    }

    // Drawer: Apply commits the draft (one filter-change per changed key),
    // Cancel discards and resets — both close the drawer.
    if (target.closest("[data-work-filter-apply]")) {
      this._applyDraft()
      return
    }
    if (target.closest("[data-work-filter-cancel]")) {
      this._resetDraft()
      this._closeDrawer()
      return
    }

    // Toolbar: sort headers cycle null → asc → desc → null; the engine
    // NEVER reorders DOM rows (N-15) — the host re-sorts server-side.
    const sortHeader = target.closest("[data-work-sort-column]")
    if (sortHeader) {
      this._cycleSort(sortHeader.dataset.workSortColumn)
      return
    }

    // Rows: a click OUTSIDE interactive descendants opens the issue
    // (CONEK-6354 stopPropagation port — N-37).
    if (target.closest(INTERACTIVE_EXCLUSION)) return
    const row = target.closest("tbody tr")
    if (!row) return
    this._openIssueFromRow(row)
  }

  _cycleSort(column) {
    const current = this._sort && this._sort.column === column ? this._sort.direction : null
    const next = current === null ? "asc" : current === "asc" ? "desc" : null
    this._sort = next ? {column, direction: next} : null
    this._paintAriaSort()
    this._emit("sort-change", {column, direction: next})
  }

  // aria-sort mirrors the engine cycle; every OTHER sortable header reads
  // "none" once anything is sorted. The th holds the sort marker in TWO
  // real shapes — the component renders it on the button INSIDE the th,
  // the fixture/host markup may carry it on the th itself — so resolve the
  // column from whichever holds it, and paint aria-sort on the th.
  _paintAriaSort() {
    const headers = Array.from(this.element.querySelectorAll("th"))
    headers.forEach((th) => {
      const holder = th.querySelector("[data-work-sort-column]")
      const column = (holder || th).dataset.workSortColumn
      if (!column) return
      const active = this._sort && this._sort.column === column ? this._sort.direction : null
      th.setAttribute("aria-sort", active || "none")
    })
  }

  // --- draft+Apply drawer (N-42) ---

  // Committed values from the drawer root's data-filter-committed JSON.
  _committedDraft() {
    const root = this.element.querySelector("[data-work-filter-drawer]")
    if (!root) return {}
    try {
      const committed = JSON.parse(root.dataset.filterCommitted || "{}")
      const draft = {}
      for (const [key, values] of Object.entries(committed)) draft[key] = new Set(values)
      return draft
    } catch {
      return {}
    }
  }

  // Checkbox toggles STAGE locally — they emit NOTHING until Apply (the
  // draft+Apply contract; a toggle that fires an event is a failure).
  _onDraftChange(event) {
    const option = event.target.closest("[data-work-filter-option]")
    if (!option) return
    const key = option.dataset.filterKey
    const value = option.dataset.filterValue
    if (!key || !value) return
    const draft = this._draft[key] || (this._draft[key] = new Set())
    if (option.checked) draft.add(value)
    else draft.delete(value)
  }

  // Apply: one filter-change per CHANGED key (full remaining value list —
  // additions and removals land together), then committed becomes the
  // draft, then the drawer closes.
  _applyDraft() {
    for (const [key, staged] of Object.entries(this._draft)) {
      const committed = new Set(this.filterValuesValue[key] || [])
      if (this._sameSet(staged, committed)) continue
      this.filterValuesValue = {...this.filterValuesValue, [key]: [...staged]}
      this._emit("filter-change", {filter_key: key, values: [...staged]})
    }
    this._closeDrawer()
  }

  // Cancel: discard the draft and reset the checkboxes to committed state.
  _resetDraft() {
    this._draft = this._committedDraft()
    this.element.querySelectorAll("[data-work-filter-option]").forEach((option) => {
      const key = option.dataset.filterKey
      const value = option.dataset.filterValue
      option.checked = Boolean(this._draft[key]?.has(value))
    })
  }

  _closeDrawer() {
    const root = this.element.querySelector("[data-work-filter-drawer] [data-controller~='ui--drawer']")
    if (!root) return
    // One close per closing cycle (F-4/N-13g): the footer buttons stay
    // clickable during the 500ms slide-out, so a second engine-issued close
    // inside the window (Apply double-click, Apply→Cancel, Esc→Cancel)
    // would hit the open-guard-less ui--drawer#close() — a second timer and
    // a duplicate drawer:close. The guard clears on that drawer's
    // drawer:close event (_onDrawerClosed). Apply's filter-change emission
    // and Cancel's draft reset both run BEFORE this method, so the bail
    // never touches commit semantics.
    if (this._closingDrawers.has(root)) return
    const controller = this.application.getControllerForElementAndIdentifier(root, "ui--drawer")
    if (!controller) return
    controller.close()
    // Apply/Cancel close the drawer too — the same closing window opens, so
    // the same one-close-per-cycle guard arms here (F-4).
    this._closingDrawers.add(root)
  }

  // A drawer's closing cycle COMPLETED (drawer:close bubbles from the drawer
  // root once its 500ms slide-out timer has hidden it): clear that drawer's
  // guard so a RE-OPENED drawer can be closed again (F-4).
  _onDrawerClosed(event) {
    if (event.target instanceof Element) this._closingDrawers.delete(event.target)
  }

  _sameSet(a, b) {
    if (a.size !== b.size) return false
    for (const value of a) if (!b.has(value)) return false
    return true
  }

  // --- keyboard (§3.5) — DOM order, RTL-safe (no physical left/right) ---

  _onKeydown(event) {
    const key = event.key
    if (key === "Escape") {
      if (this._onEscape(event)) event.stopPropagation()
      return
    }
    // A modal surface (open columns picker / visible filter drawer) owns
    // the keyboard: only the Escape branch above runs. Row shortcuts must
    // not yank focus behind an open dialog (j/k/Enter/Space/x and / all
    // gate here; the docs' keyboard table applies to the idle surface).
    if (this._modalSurfaceOpen()) return
    // Typing targets never become row gestures (except Escape, above).
    if (event.target.closest(INPUT_EXCLUSION)) return

    const row = event.target.closest && event.target.closest("tbody tr")
    switch (key) {
      case "j":
        this._rovingFocus(1)
        break
      case "k":
        this._rovingFocus(-1)
        break
      case "Enter":
      case "o":
        // Enter/o open the FOCUSED row's issue.
        if (row) {
          event.preventDefault()
          this._openIssueFromRow(row)
        }
        break
      case " ":
        // Space toggles selection THROUGH the W3 seam — the navigator adds
        // no selection state of its own (W11-D6).
        if (row) {
          event.preventDefault()
          this._selectionRequest(row, "toggle")
        }
        break
      case "x":
        // x toggles BULK membership for the focused row.
        if (row) {
          this._selectionRequest(row, "bulk-toggle")
        }
        break
      case "/":
        // `/` focuses the in-page filter box — NEVER the search surface
        // (search-request fires on `.` in the ui--work-search controller;
        // the navigator does not own it, A3/N-07).
        event.preventDefault()
        this.element.querySelector("[data-work-filter-input]")?.focus()
        break
    }
  }

  // Escape closes ONE open surface per press — nearest-first (the columns
  // picker before the filter drawer), and NEVER a second surface behind it:
  //
  //   - An inner controller that already CONSUMED this Escape has served
  //     the nearest surface. The real ui--dropdown-menu's content-level
  //     handler closes the picker and preventDefaults WITHOUT stopping
  //     propagation, so the bubbled event arrives at the engine with the
  //     picker already closed — without this check the engine would fall
  //     through to the drawer and one press would close BOTH (F-5). The
  //     engine closes nothing further and still consumes the press, so the
  //     drawer's own document-level handleEscape cannot act behind it.
  //   - A drawer in its 500ms closing window still matches :not(.hidden);
  //     a second Esc inside the window must NOT re-run close() —
  //     ui--drawer#close() has no open-guard, so a second call means a
  //     second timer and a duplicate drawer:close (F-4). One close per
  //     closing cycle: the guard arms on every close this engine issues and
  //     clears on that drawer's drawer:close event (see _onDrawerClosed).
  //     The press is still CONSUMED in the window — a surface is visually
  //     open (`_modalSurfaceOpen` agrees) and the event must not reach the
  //     drawer's document-level handler either.
  //
  // With nothing open the event is left untouched for the document-level
  // handlers. close() is the drawer's real dismiss API (guarded on
  // dismissible — the navigator's drawer is dismissible:true by default).
  _onEscape(event) {
    if (event.defaultPrevented) return true

    const picker = this.element.querySelector(PICKER_OPEN_SELECTOR)
    if (picker) {
      const controller = this.application.getControllerForElementAndIdentifier(
        picker.closest("[data-controller~='ui--dropdown-menu']"), "ui--dropdown-menu")
      if (!controller) return false
      controller.closeMenu()
      return true
    }
    const drawerRoot = this.element.querySelector(DRAWER_OPEN_SELECTOR)
    if (!drawerRoot) return false
    if (this._closingDrawers.has(drawerRoot)) return true
    const controller = this.application.getControllerForElementAndIdentifier(drawerRoot, "ui--drawer")
    if (!controller) return false
    controller.close()
    this._closingDrawers.add(drawerRoot)
    return true
  }

  // §3.5 gate: a modal surface is open when either open-surface probe
  // matches (the same probes _onEscape acts on — one definition of "open").
  _modalSurfaceOpen() {
    return Boolean(
      this.element.querySelector(PICKER_OPEN_SELECTOR) ||
      this.element.querySelector(DRAWER_OPEN_SELECTOR)
    )
  }

  // --- rows (engine-owned roving tabindex — the row is the host's, so the
  // tabindex is SET programmatically, never rendered as row metadata) ---

  _rows() {
    return Array.from(this.element.querySelectorAll("tbody tr"))
  }

  _initRovingTabindex() {
    const rows = this._rows()
    rows.forEach((row, index) => row.setAttribute("tabindex", index === 0 ? "0" : "-1"))
    this._rowOrdinal = rows.length > 0 ? 0 : -1
  }

  // j/k: move focus one row forward/backward in DOM order (RTL-safe: no
  // physical left/right is ever used). Standard roving: the focused row
  // becomes tabindex 0, the rest -1.
  _rovingFocus(step) {
    const rows = this._rows()
    if (rows.length === 0) return
    const current = document.activeElement && rows.indexOf(document.activeElement)
    const base = current === -1 ? Math.max(this._rowOrdinal, 0) : current
    const next = Math.min(Math.max(base + step, 0), rows.length - 1)
    if (next === base && current !== -1) return
    rows.forEach((row, index) => row.setAttribute("tabindex", index === next ? "0" : "-1"))
    rows[next].focus()
    this._rowOrdinal = next
  }

  // Row id comes from the CARD inside the row (data-issue-id) — the
  // navigator adds no row metadata of its own (N-35 compose discipline).
  _issueIdFromRow(row) {
    return row.querySelector("[data-issue-id]")?.dataset.issueId
  }

  _openIssueFromRow(row) {
    const issueId = this._issueIdFromRow(row)
    if (!issueId) return
    this._emit("issue-open", {issue_id: issueId})
  }

  // W3 seam (W11-D6 stub-and-swap): until the selection contract merges,
  // the navigator dispatches selection-request and adds NO state of its
  // own. Swap note lives in docs/work/navigator.md.
  _selectionRequest(row, mode) {
    const issueId = this._issueIdFromRow(row)
    if (!issueId) return
    this._emit("selection-request", {issue_id: issueId, mode})
  }

  // --- plumbing ---

  _parseSort(raw) {
    if (!raw) return null
    const [column, direction] = raw.split(":")
    if (!column || (direction !== "asc" && direction !== "desc")) return null
    return {column, direction}
  }

  // Grammar: ui--work:<verb>, bubbling CustomEvent on the root (§3.2).
  _emit(verb, detail) {
    this.element.dispatchEvent(new CustomEvent(`ui--work:${verb}`, {
      bubbles: true,
      cancelable: true,
      detail,
    }))
  }

  _announce(message) {
    const announcer = this.hasAnnouncerTarget ? this.announcerTarget : null
    if (!announcer) return
    announcer.textContent = message
  }
}
